"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTooltip = exports.popperElementBaseClass = void 0;
const React = require("react");
const core_1 = require("@popperjs/core");
const hoverEvents = {
    show: ['mouseenter', 'focus'],
    hide: ['mouseleave', 'blur'],
};
const clickEvent = 'click';
exports.popperElementBaseClass = 'tooltip-content';
const useTooltip = (trigger = 'hover', options = {}, { isDisabled = false, activeClassName = 'tooltip-active', onShow, onHide } = {}) => {
    const [isOpened, setIsOpened] = React.useState(false);
    const [referenceElement, setReferenceElement] = React.useState(null);
    const [popperElement, setPopperElement] = React.useState(null);
    const popperInstanceRef = React.useRef(null);
    const handleCreatePopper = React.useCallback(() => {
        var _a;
        const mergedOptions = Object.assign(Object.assign({}, options), { modifiers: [core_1.preventOverflow, core_1.flip, ...((_a = options === null || options === void 0 ? void 0 : options.modifiers) !== null && _a !== void 0 ? _a : [])] });
        popperInstanceRef.current = (0, core_1.createPopper)(referenceElement, popperElement, mergedOptions);
    }, [options, popperElement, referenceElement]);
    const handleDestroyPopper = React.useCallback(() => {
        var _a;
        (_a = popperInstanceRef.current) === null || _a === void 0 ? void 0 : _a.destroy();
        popperInstanceRef.current = null;
    }, [popperInstanceRef]);
    const handleShowPopper = React.useCallback(() => {
        setIsOpened(true);
        popperElement === null || popperElement === void 0 ? void 0 : popperElement.classList.add(activeClassName);
        if (onShow) {
            onShow();
        }
    }, [activeClassName, onShow, popperElement === null || popperElement === void 0 ? void 0 : popperElement.classList]);
    const handleHidePopper = React.useCallback(() => {
        setIsOpened(false);
        popperElement === null || popperElement === void 0 ? void 0 : popperElement.classList.remove(activeClassName);
        handleDestroyPopper();
        if (onHide) {
            onHide();
        }
    }, [popperElement === null || popperElement === void 0 ? void 0 : popperElement.classList, activeClassName, handleDestroyPopper, onHide]);
    const handleOnDocumentClick = React.useCallback((event) => {
        if (isOpened &&
            popperElement &&
            referenceElement &&
            !popperElement.contains(event.target) &&
            !referenceElement.contains(event.target)) {
            handleHidePopper();
        }
    }, [isOpened, popperElement, referenceElement, handleHidePopper]);
    // handle click outside
    React.useLayoutEffect(() => {
        const isClickTrigger = trigger === clickEvent;
        if (isClickTrigger) {
            document.body.addEventListener('mousedown', handleOnDocumentClick);
            return () => {
                document.body.removeEventListener('mousedown', handleOnDocumentClick);
            };
        }
    }, [trigger, handleOnDocumentClick]);
    // set base global tooltip class to popper
    React.useLayoutEffect(() => {
        if (popperElement && !popperElement.classList.contains(exports.popperElementBaseClass)) {
            popperElement.classList.add(exports.popperElementBaseClass);
        }
    }, [popperElement]);
    // create popper when isOpened became true
    React.useLayoutEffect(() => {
        if (isOpened && popperElement) {
            handleCreatePopper();
        }
    }, [isOpened, popperElement, handleCreatePopper]);
    // initialize tooltip and alignment logic
    React.useLayoutEffect(() => {
        if (isDisabled || !referenceElement) {
            return;
        }
        const toggle = () => {
            if (isOpened) {
                handleHidePopper();
            }
            else {
                handleShowPopper();
            }
        };
        switch (trigger) {
            case 'hover':
                hoverEvents.show.forEach(event => referenceElement.addEventListener(event, handleShowPopper));
                hoverEvents.hide.forEach(event => referenceElement.addEventListener(event, handleHidePopper));
                return () => {
                    hoverEvents.show.forEach(event => referenceElement.removeEventListener(event, handleShowPopper));
                    hoverEvents.hide.forEach(event => referenceElement.removeEventListener(event, handleHidePopper));
                };
            case 'click':
                referenceElement.addEventListener(clickEvent, toggle);
                return () => {
                    referenceElement.removeEventListener(clickEvent, toggle);
                };
            default:
                console.error(`Tooltip trigger ${trigger} is not supported`);
        }
    }, [
        referenceElement,
        popperElement,
        handleHidePopper,
        popperInstanceRef,
        options,
        isDisabled,
        handleShowPopper,
        isOpened,
        trigger,
        activeClassName,
    ]);
    return {
        isOpened,
        reference: {
            ref: setReferenceElement,
        },
        tooltip: {
            ref: setPopperElement,
        },
        instance: popperInstanceRef === null || popperInstanceRef === void 0 ? void 0 : popperInstanceRef.current,
        show: handleShowPopper,
        hide: handleHidePopper,
    };
};
exports.useTooltip = useTooltip;
